import { Options, Rectangle, PolyLine, Polygon, Circle, Ellipse, Icon, RegionLabel, IconWithText } from "./Markers.js";
import { S } from "../Squaremap.js";
import { fetchMarkerLayersBySource } from "./markerLayers.js";
import { notifyMapWorldChanged } from "../../react/bridge/mapBridge.js";
import L from "leaflet";

class World {
    /** @type {string} */
    name;
    /** @type {string} */
    display_name;
    /** @type {Map<string, L.LayerGroup>} */
    markerLayers;
    /** @type {WorldSettings_PlayerTracker} */
    player_tracker;
    /** @type {number} */
    marker_update_interval;
    /** @type {number} */
    tiles_update_interval;
    /** @type {boolean} */
    staticNeedsMarkerTick;
    /** @type {WorldSettings_Zoom} */
    zoom;
    /** @type {WorldSettings_Spawn} */
    spawn;

    /**
     * @param json {Settings_World}
     */
    constructor(json) {
        this.name = json.name;
        this.order = json.order;
        this.icon = json.icon;
        this.type = json.type;
        this.display_name = json.display_name;
        this.markerLayers = new Map();
        this.player_tracker = {};
        this.marker_update_interval = 5;
        this.tiles_update_interval = 15;
        this.staticNeedsMarkerTick = false;
    }
    tick() {
        // refresh map tile layer
        if (!S.staticMode && S.tick_count % this.tiles_update_interval === 0) {
            S.layerControl.updateTileLayer();
        }
        // load and draw markers
        if (!S.staticMode && S.tick_count % this.marker_update_interval === 0) {
            this.tickMarkers();
        }
        if (S.staticMode && this.staticNeedsMarkerTick) {
            this.tickMarkers();
            this.staticNeedsMarkerTick = false;
        }
    }
    tickMarkers() {
        fetchMarkerLayersBySource(this.name, this.type).then(({ squaremap, weiranGis }) => {
            if (this === S.worldList.curWorld) {
                this.markersFromSources(squaremap, weiranGis);
            }
        });
    }
    unload() {
        S.playerList.clearPlayerMarkers();
        const keys = Array.from(this.markerLayers.keys());
        for (let i = 0; i < keys.length; i++) {
            const layer = this.markerLayers.get(keys[i]);
            S.layerControl.controls.removeLayer(layer);
            layer.remove();
            this.markerLayers.delete(keys[i]);
        }
    }
    /**
     * @param callback {(world: World) => void}
     */
    load(callback) {
        S.getJSON(
            `tiles/${this.name}/settings.json`,
            /** @param {WorldSettings} json */
            (json) => {
                this.player_tracker = json.player_tracker;
                this.zoom = json.zoom;
                this.spawn = json.spawn;
                this.marker_update_interval = json.marker_update_interval;
                this.tiles_update_interval = json.tiles_update_interval;
                this.staticNeedsMarkerTick = true;

                // set the scale for our projection calculations
                S.setScale(this.zoom.max);

                // set center and zoom
                S.centerOn(this.spawn.x, this.spawn.z, this.zoom.def)
                    .setMinZoom(0) // extra zoom out doesn't work :(
                    .setMaxZoom(this.zoom.max + this.zoom.extra);
                S.scaleBar?.update();

                // update page title
                document.title = S.title.replace(/{world}/g, this.display_name);

                // setup background
                document.getElementById("map").style.background = this.getBackground();

                // setup tile layers
                S.layerControl.setupTileLayers(this);

                // force clear player markers
                S.playerList.clearPlayerMarkers();

                // tick now, reset counter
                S.tick_count = 0;
                S.tick();

                // force clear player markers
                S.playerList.clearPlayerMarkers();

                if (callback != null) {
                    callback(this);
                }

                notifyMapWorldChanged();
            },
        );
    }
    getBackground() {
        switch (this.type) {
            case "nether":
                return "url('images/nether_sky.png')";
            case "the_end":
                return "url('images/end_sky.png')";
            case "normal":
            default:
                return "url('images/overworld_sky.png')";
        }
    }
    /**
     * @param {any[]} squaremapLayers
     * @param {any[]} weiranGisLayers
     */
    markersFromSources(squaremapLayers, weiranGisLayers) {
        /** @type {Set<string>} */
        const activeIds = new Set();
        for (const entry of squaremapLayers) {
            if (entry?.id != null) {
                activeIds.add(entry.id);
            }
        }
        for (const entry of weiranGisLayers) {
            if (entry?.id != null) {
                activeIds.add(entry.id);
            }
        }

        for (const [id, layer] of this.markerLayers) {
            if (!activeIds.has(id)) {
                S.layerControl.removeOverlay(layer);
                this.markerLayers.delete(id);
            }
        }

        for (const entry of squaremapLayers) {
            this.applyMarkerEntry(entry, "squaremap");
        }
        for (const entry of weiranGisLayers) {
            this.applyMarkerEntry(entry, entry.controlSource ?? "geo-region");
        }
    }
    /**
     * @param {any} entry
     * @param {'squaremap' | 'geo-region' | 'unit-point'} source
     */
    applyMarkerEntry(entry, source) {
        if (entry == null || entry.id == null) {
            return;
        }

        let layer = this.markerLayers.get(entry.id);
        if (layer != null) {
            if (layer.timestamp === entry.timestamp && layer.source === source) {
                return;
            }
            S.layerControl.removeOverlay(layer);
        }

        layer = new L.LayerGroup();
        layer.order = entry.order;
        layer.id = entry.id;
        layer.timestamp = entry.timestamp;
        layer.source = source;
        layer.setZIndex(entry.z_index);
        this.markerLayers.set(layer.id, layer);

        if (entry.control === true) {
            S.layerControl.addOverlay(entry.name, layer, entry.hide, source, entry.legend ?? null);
        }

        for (const shape in entry.markers) {
            let marker;
            const opts = new Options(entry.markers[shape]);
            switch (opts.pop("type")) {
                case "rectangle":
                    marker = new Rectangle(opts);
                    break;
                case "polyline":
                    marker = new PolyLine(opts);
                    break;
                case "polygon":
                    marker = new Polygon(opts);
                    break;
                case "circle":
                    marker = new Circle(opts);
                    break;
                case "ellipse":
                    marker = new Ellipse(opts);
                    break;
                case "icon":
                    marker = new Icon(opts);
                    break;
                case "label":
                    marker = new RegionLabel(opts);
                    break;
                case "iconWithText":
                    marker = new IconWithText(opts);
                    break;
            }
            if (marker != null) {
                marker.addTo(layer);
            }
        }

        bindLayerZoomVisibility(layer, entry.minZoom, entry.maxZoom);
    }
}

/**
 * @param {L.Layer} marker
 * @param {boolean} visible
 */
function setMarkerZoomVisibility(marker, visible) {
    if (typeof marker.setOpacity === "function") {
        marker.setOpacity(visible ? 1 : 0);
    }
    marker.options.interactive = visible;
    if (typeof marker.getElement !== "function") {
        return;
    }
    const el = marker.getElement();
    if (!(el instanceof HTMLElement)) {
        return;
    }
    el.classList.toggle("weiran-poi-marker--hidden", !visible);
    el.style.pointerEvents = visible ? "" : "none";
    const poiEl = el.querySelector(".weiran-poi");
    if (poiEl instanceof HTMLElement) {
        poiEl.tabIndex = visible ? 0 : -1;
    }
}

/**
 * @param {L.LayerGroup} layer
 * @param {number | null | undefined} minZoom
 * @param {number | null | undefined} maxZoom
 */
function bindLayerZoomVisibility(layer, minZoom, maxZoom) {
    if (minZoom == null && maxZoom == null) {
        return;
    }

    /** @param {number} zoom */
    const isVisibleAtZoom = (zoom) => {
        if (minZoom != null && zoom < minZoom) {
            return false;
        }
        if (maxZoom != null && zoom > maxZoom) {
            return false;
        }
        return true;
    };

    const update = () => {
        if (!S.map.hasLayer(layer)) {
            return;
        }
        const visible = isVisibleAtZoom(S.map.getZoom());
        layer.eachLayer((marker) => {
            setMarkerZoomVisibility(marker, visible);
        });
    };

    layer.on("add", update);
    S.map.on("zoom", update);
    S.map.on("zoomend", update);
    layer.on("remove", () => {
        S.map.off("zoom", update);
        S.map.off("zoomend", update);
    });
}

export { World };
